import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  useTheme,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { socialComplete } from '../../services/auth';
import { encrypt } from '../../utils/crypto';
import { useAuth } from '../../contexts/AuthContext';
import { LegalInline } from '../../components/Legal/LegalPage';
import { rememberReturnTo, returnStateFrom } from '../../utils/authRedirect';

const SocialCompletePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  const state = location.state || {};
  const [phone, setPhone] = useState('');
  const [phoneCode] = useState('90');
  const [bornYear, setBornYear] = useState('2000-01-01');
  const [gender, setGender] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Ticari elektronik ileti onayı — isteğe bağlı, varsayılan kapalı
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (!state.provider || !state.token) {
      navigate('/login', { replace: true });
    }
  }, [state.provider, state.token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      toast.error(t('auth.socialComplete.errors.phone'));
      return;
    }
    if (!bornYear) {
      toast.error(t('auth.socialComplete.errors.birthDate'));
      return;
    }
    if (!gender) {
      toast.error(t('auth.socialComplete.errors.gender'));
      return;
    }

    const birth = new Date(bornYear);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
    if (age < 16) {
      toast.error(t('auth.socialComplete.errors.age'));
      return;
    }
    if (!acceptTerms) {
      toast.error(t('auth.register.acceptTerms'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await socialComplete({
        provider: state.provider,
        token: state.token,
        phone: phone.replace(/\D/g, ''),
        phoneCode,
        bornYear: birth.toISOString(),
        gender: Number(gender),
        name: state.name,
        lastName: state.lastName,
        marketingConsent,
      });

      if (response?.statusCode === 200 && response.data?.token) {
        localStorage.removeItem('activationTimer');
        localStorage.removeItem('activationTimerExpiry');
        const encryptedToken = encrypt(response.data.token);
        if (encryptedToken) {
          localStorage.setItem('authToken', encryptedToken);
        } else {
          localStorage.setItem('authToken', response.data.token);
        }

        setUser({
          id: response.data.id,
          firstName: response.data.name,
          lastName: response.data.lastName,
          isActivated: false,
        });

        toast.success(t('auth.socialComplete.success'));
        const returnState = returnStateFrom(location);
        rememberReturnTo(returnState?.from);
        navigate('/activation', { state: returnState });
      } else {
        toast.error(response?.message || t('auth.socialComplete.errors.submit'));
      }
    } catch (error) {
      toast.error(t('auth.socialComplete.errors.submit'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 8, px: 2 }}>
      <Container maxWidth="sm">
        <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2 }}>
          <Typography variant="h4" align="center" gutterBottom>
            {t('auth.socialComplete.title')}
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.socialComplete.subtitle')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('auth.login.email.label')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            {state.email}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('auth.socialComplete.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="5xxxxxxxxx"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>+{phoneCode}</Typography>,
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              type="date"
              label={t('auth.socialComplete.birthDate')}
              InputLabelProps={{ shrink: true }}
              value={bornYear}
              onChange={(e) => setBornYear(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              select
              label={t('auth.socialComplete.gender')}
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="201">{t('auth.socialComplete.male')}</MenuItem>
              <MenuItem value="104">{t('auth.socialComplete.female')}</MenuItem>
            </TextField>

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
              control={<Checkbox required checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />}
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5 }}>
                  <LegalInline text={t('auth.register.consentTerms')} linkTarget="_blank" />
                </Typography>
              }
            />
            <FormControlLabel
              sx={{ mt: 1, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
              control={<Checkbox checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} />}
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5 }}>
                  <LegalInline text={t('auth.register.consentMarketing')} linkTarget="_blank" />
                </Typography>
              }
            />
            <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>
              <LegalInline text={t('auth.register.consentKvkk')} linkTarget="_blank" />
            </Typography>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 3, py: 1.5, borderRadius: 2, backgroundColor: theme.palette.primary.main }}
            >
              {t('auth.socialComplete.submit')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SocialCompletePage;
